/* eslint-disable camelcase */

import TagManager from 'react-gtm-module';
import MiddlewareEntities from '../../../../constants/MiddlewareEntities';
import { PaymentOptionsType } from '../../../../constants/PaymentOptions';
import { IProductPrepared, PayloadWithProductsListAndSelectedProduct } from '../../../../entities/product/types';
import {
  createListName,
  DEFAULT_GA4_PROPS,
  getOverviewGa4ItemValues,
  getOverviewGa4ParticipationValues,
  getOverviewGa4TariffValues,
  mapOrderItemsToGa4Items,
  mapProductToGa4Item,
} from '../../../../utilites/ga4utils';
import { removeDotFromPrice } from '../../../../utilites/util';
import { AppState } from '../../rootReducers';
import { PushDeviceListToAnalyticsActionPayload } from '../types';
import { GA4Event, GA4EventObject, GA4EventType, GA4Item, GA4PromotionDetails, GA4PPRContentData, GA4TopupAnalytics } from './types';
import { IBasketOrderItem } from '../../../../entities/manhattan/order/types';
import { deviceTypes } from '../../../../constants/TelcoDeviceTypes';
import { getFlatStructureOfBasketItems } from '../../../../utilites/order';

abstract class IAnalyticsMiddlewareGA4Proxy {

  public static viewItemList: (state: AppState, { products }: PushDeviceListToAnalyticsActionPayload) => void;
  public static selectItem: (state: AppState, { product, products }: PayloadWithProductsListAndSelectedProduct) => void;
  public static viewItem: (state: AppState) => void;
  public static addToCart: (state: AppState) => void;
  public static addToWishlist: (state: AppState, productId: number) => void;
  public static viewCart: (state: AppState) => void;
  public static removeFromCartAll: (state: AppState) => void;
  public static beginCheckout: (state: AppState) => void;
  public static selectPaymentOption: (state: AppState, paymentOption: PaymentOptionsType) => void;
  public static addShippingInfo: (state: AppState, shippingTier: string) => void;
  public static purchase: (state: AppState) => void;
  public static refund: (state: AppState) => void;
  public static viewPromotion: (state: AppState, promotionDetails: GA4PromotionDetails, items: GA4Item[]) => void;
  public static selectPromotion: (state: AppState, promotionDetails: GA4PromotionDetails, item: GA4Item) => void;
  public static selectInsurance: (state: AppState, items: GA4Item[]) => void;
  public static topupStep: (state: AppState, data: GA4TopupAnalytics) => void;
  public static pprInteraction: (state: AppState, eventName: string, contentData: GA4PPRContentData) => void;

}

const RSD = 'RSD';

export default class AnalyticsMiddlewareGA4Proxy extends IAnalyticsMiddlewareGA4Proxy {

  public static viewItemList = (state: AppState, { products }: PushDeviceListToAnalyticsActionPayload) => {
    const gtmUserId = state.session.gtmData?.gtmUserId;
    const searchParams = state.searchParams.productList;

    const ga4ViewItemListEvent: GA4EventObject = {
      name: 'view_item_list',
      params: {
        item_list_name: createListName(searchParams, products[0]),
        item_list_id: '',
        items: products.slice(0, 20).map(mapProductToGa4Item),
      },
    };

    TagManager.dataLayer({
      dataLayer: {
        user_id: gtmUserId,
        events: [ga4ViewItemListEvent],
        ...DEFAULT_GA4_PROPS,
      } as GA4Event,
    });
  };

  public static selectItem(state: AppState, { product, products }: PayloadWithProductsListAndSelectedProduct) {
    const gtmUserId = state.session.gtmData?.gtmUserId;
    const searchParams = state.searchParams.productList;
    const position = products.findIndex((productItem: IProductPrepared) => {
      const productItemId = productItem.phoneId ?? productItem.id;
      const id = product.phoneId ?? product.id;

      return productItemId === id;
    });

    const ga4SelectItemEvent: GA4EventObject = {
      name: 'select_item',
      params: {
        item_list_name: createListName(searchParams, product),
        item_list_id: '',
        items: [mapProductToGa4Item(product, position)],
      },
    };

    TagManager.dataLayer({
      dataLayer: {
        user_id: gtmUserId,
        events: [ga4SelectItemEvent],
        ...DEFAULT_GA4_PROPS,
      } as GA4Event,
    });
  }

  public static viewItem(state: AppState) {
    const gtmUserId = state.session.gtmData?.gtmUserId;
    const {
      productData,
      product: { item },
    } = state.productData;

    const productItem = getOverviewGa4ItemValues(item, productData);
    const tariffItem = getOverviewGa4TariffValues(item, productData);
    const participationItem = getOverviewGa4ParticipationValues(productData);

    const viewedItemValue =
      productItem.price + tariffItem.price * (tariffItem.quantity ?? 1) + Number(participationItem?.price);

    const nonNullItems = [productItem, tariffItem, participationItem].filter((isItemTruthy) => Boolean(isItemTruthy));

    const ga4SelectItemEvent: GA4EventObject = {
      name: 'view_item',
      params: {
        currency: RSD,
        value: viewedItemValue,
        items: nonNullItems as GA4Item[],
      },
    };

    TagManager.dataLayer({
      dataLayer: {
        user_id: gtmUserId,
        events: [ga4SelectItemEvent],
        ...DEFAULT_GA4_PROPS,
      } as GA4Event,
    });
  }

  public static addToCart(state: AppState) {
    const gtmUserId = state.session.gtmData?.gtmUserId;
    const {
      productData,
      product: { item },
    } = state.productData;

    const productItem = getOverviewGa4ItemValues(item, productData);
    const tariffItem = getOverviewGa4TariffValues(item, productData);
    const participationItem = getOverviewGa4ParticipationValues(productData);

    const viewedItemValue =
      productItem.price + tariffItem.price * (tariffItem.quantity ?? 1) + Number(participationItem?.price);

    const nonNullItems = [productItem, tariffItem, participationItem].filter((isItemTruthy) => Boolean(isItemTruthy));

    const ga4SelectItemEvent: GA4EventObject = {
      name: 'add_to_cart',
      params: {
        currency: RSD,
        value: viewedItemValue,
        items: nonNullItems as GA4Item[],
      },
    };

    const ga4ViewCart: GA4EventObject = {
      name: 'view_cart',
      params: {
        items: nonNullItems as GA4Item[],
      },
    };

    TagManager.dataLayer({
      dataLayer: {
        user_id: gtmUserId,
        events: [ga4SelectItemEvent, ga4ViewCart],
        ...DEFAULT_GA4_PROPS,
      } as GA4Event,
    });
  }

  public static addToWishlist(state: AppState, productId: number) {
    const { searchParams } = state;
    const { entity } = state.ui;
    const gtmUserId = state.session.gtmData?.gtmUserId;

    const ga4AddToWishlistEvent: GA4EventObject = {
      name: 'add_to_wishlist',
      params: {
        item_list_name: '',
        item_list_id: '',
        items: [],
      },
    };

    if (entity === MiddlewareEntities.PRODUCTS) {
      const productFromList = state.products.items.find(
        (p: IProductPrepared) => Number(p.id) === productId || Number(p.phoneId) === productId
      );
      const productIndex = state.products.items.findIndex(
        (p: IProductPrepared) => Number(p.id) === productId || Number(p.phoneId) === productId
      );
      ga4AddToWishlistEvent.params.item_list_name = createListName(searchParams.productList, productFromList);
      ga4AddToWishlistEvent.params.items = [mapProductToGa4Item(productFromList, productIndex)];
    }

    if (entity === MiddlewareEntities.PRODUCT) {
      const {
        productData,
        product: { item },
      } = state.productData;
      ga4AddToWishlistEvent.params.items = [getOverviewGa4ItemValues(item, productData)];
    }

    TagManager.dataLayer({
      dataLayer: {
        user_id: gtmUserId,
        events: [ga4AddToWishlistEvent],
        ...DEFAULT_GA4_PROPS,
      } as GA4Event,
    });
  }

  public static viewCart(state: AppState) {
    const gtmUserId = state.session.gtmData.gtmUserId;

    TagManager.dataLayer({
      dataLayer: {
        user_id: gtmUserId,
        events: [
          {
            name: 'view_cart',
            params: {},
          },
        ],
        ...DEFAULT_GA4_PROPS,
      } as GA4Event,
    });
  }

  public static removeFromCartAll(state: AppState) {
    const gtmUserId = state.session.gtmData?.gtmUserId;
    const items = getFlatStructureOfBasketItems(state.manhattanOrder.order.basket);

    TagManager.dataLayer({
      dataLayer: {
        user_id: gtmUserId,
        events: [
          {
            name: 'remove_from_cart',
            params: {
              items: mapOrderItemsToGa4Items(items),
            },
          },
        ],
        ...DEFAULT_GA4_PROPS,
      } as GA4Event,
    });
  }

  public static beginCheckout(state: AppState) {
    const gtmUserId = state.session.gtmData?.gtmUserId;
    const {
      productData,
      product: { item },
    } = state.productData;

    const productItem = getOverviewGa4ItemValues(item, productData);
    const tariffItem = getOverviewGa4TariffValues(item, productData);
    const participationItem = getOverviewGa4ParticipationValues(productData);

    const viewedItemValue =
      productItem.price + tariffItem.price * (tariffItem.quantity ?? 1) + Number(participationItem?.price);

    const nonNullItems = [productItem, tariffItem, participationItem].filter((isItemTruthy) => Boolean(isItemTruthy));

    const ga4SelectItemEvent: GA4EventObject = {
      name: 'begin_checkout',
      params: {
        currency: RSD,
        value: viewedItemValue,
        items: nonNullItems as GA4Item[],
      },
    };

    const ga4ViewCart: GA4EventObject = {
      name: 'view_cart',
      params: {
        items: nonNullItems as GA4Item[],
      },
    };

    TagManager.dataLayer({
      dataLayer: {
        user_id: gtmUserId,
        events: [ga4SelectItemEvent, ga4ViewCart],
        ...DEFAULT_GA4_PROPS,
      } as GA4Event,
    });
  }

  public static selectPaymentOption(state: AppState, paymentOption: PaymentOptionsType) {
    const gtmUserId = state.session.gtmData?.gtmUserId;

    const ga4SelectPaymentOptionEvent: GA4EventObject = {
      name: 'add_payment_info',
      params: {
        payment_type: paymentOption,
      },
    };

    TagManager.dataLayer({
      dataLayer: {
        user_id: gtmUserId,
        events: [ga4SelectPaymentOptionEvent],
        ...DEFAULT_GA4_PROPS,
      } as GA4Event,
    });
  }

  public static addShippingInfo(state: AppState, shippingTier: string) {
    const gtmUserId = state.session.gtmData?.gtmUserId;

    const ga4SelectPaymentOptionEvent: GA4EventObject = {
      name: 'add_shipping_info',
      params: {
        price: 0,
        currency: RSD,
        shipping_tier: shippingTier,
      },
    };

    TagManager.dataLayer({
      dataLayer: {
        user_id: gtmUserId,
        events: [ga4SelectPaymentOptionEvent],
        ...DEFAULT_GA4_PROPS,
      } as GA4Event,
    });
  }

  public static purchase(state: AppState) {
    const gtmUserId = state.session.gtmData?.gtmUserId;
    const items = getFlatStructureOfBasketItems(state.manhattanOrder.order.basket);
    const { order } = state.manhattanOrder;
    const isSimOnly = items.filter((item: IBasketOrderItem) => deviceTypes.includes(item.type)).length <= 0;
    const affiliation = isSimOnly ? 'PAKET' : 'UREĐAJ + PAKET';
    const ga4Items = mapOrderItemsToGa4Items(items);
    const value = ga4Items.reduce((sum, item) => sum + Number(item.price), 0);

    const ga4PurchaseEvent: GA4EventObject = {
      name: 'purchase',
      params: {
        transaction_id: order.order?.referenceId,
        affiliation,
        coupon: '',
        value,
        tax: 0,
        shipping: 0,
        currency: RSD,
        items: ga4Items,
      },
    };

    TagManager.dataLayer({
      dataLayer: {
        user_id: gtmUserId,
        events: [ga4PurchaseEvent],
        ...DEFAULT_GA4_PROPS,
      } as GA4Event,
    });
  }

  public static refund(state: AppState) {
    const gtmUserId = state.session.gtmData?.gtmUserId;
    const items = getFlatStructureOfBasketItems(state.manhattanOrder.order.basket);
    const subtotal = state.manhattanOrder.order.basket.totalOnCheckout;
    const { order } = state.manhattanOrder;

    const ga4RefundEvent: GA4EventObject = {
      name: 'refund',
      params: {
        transaction_id: order.order?.referenceId,
        coupon: '',
        value: removeDotFromPrice(subtotal),
        tax: 0,
        shipping: 0,
        currency: RSD,
        items: mapOrderItemsToGa4Items(items),
      },
    };

    TagManager.dataLayer({
      dataLayer: {
        user_id: gtmUserId,
        events: [ga4RefundEvent],
        ...DEFAULT_GA4_PROPS,
      } as GA4Event,
    });
  }

  public static viewPromotion(state: AppState, promotionDetails: GA4PromotionDetails, items: GA4Item[]) {
    const gtmUserId = state.session.gtmData?.gtmUserId;

    const ga4ViewPromotionEvent: GA4EventObject = {
      name: 'view_promotion',
      params: {
        ...promotionDetails,
        items,
      },
    };

    TagManager.dataLayer({
      dataLayer: {
        user_id: gtmUserId,
        events: [ga4ViewPromotionEvent],
        ...DEFAULT_GA4_PROPS,
      } as GA4Event,
    });
  }

  public static selectPromotion(state: AppState, promotionDetails: GA4PromotionDetails, item: GA4Item) {
    const gtmUserId = state.session.gtmData?.gtmUserId;

    const ga4SelectPromotionEvent: GA4EventObject = {
      name: 'select_promotion',
      params: {
        ...promotionDetails,
        item,
      },
    };

    TagManager.dataLayer({
      dataLayer: {
        user_id: gtmUserId,
        events: [ga4SelectPromotionEvent],
        ...DEFAULT_GA4_PROPS,
      } as GA4Event,
    });
  }

  public static selectInsurance(state: AppState, items: GA4Item[]) {
    const gtmUserId = state.session.gtmData?.gtmUserId;

    const ga4SelectInsuranceEvent: GA4EventObject = {
      name: 'add_to_cart',
      params: {
        items,
        currency: RSD,
      },
    };

    TagManager.dataLayer({
      dataLayer: {
        user_id: gtmUserId,
        events: [ga4SelectInsuranceEvent],
        ...DEFAULT_GA4_PROPS,
      } as GA4Event,
    });
  }

  public static topupStep(state: AppState, data: GA4TopupAnalytics) {
    const { step, amount, transactionId, topupType } = data;
    const gtmUserId = state.session.gtmData?.gtmUserId;

    const getInteractionType = () => {
      return Boolean(
        (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
          // @ts-expect-error - Fallback for IOS
          window.navigator.standalone
      ).valueOf()
        ? 'pwa'
        : 'web';
    };

    const basicUserProperties = {
      user_id: gtmUserId,
      user_actualTariffName: '',
      user_carrierType: '',
      user_customerType: '',
      user_interactionType: getInteractionType(),
      user_propertyType: 'wsh',
      user_planType: '',
      currency: 'RSD',
      transaction_id: '',
      value: 0,
      tax: 0,
      shipping: 0,
    };

    const topupData = {
      item_name: 'Prepaid Top-Up',
      affiliation: 'Webshop E2E',
      item_brand: 'Yettel',
      item_category: 'Packages',
      item_category2: 'Prepaid Top-Up',
      item_category3: '',
      item_category4: 'Prepaid',
      item_category5: 'Consumer',
      item_variant: '',
      price: 0,
      quantity: 1,
    };

    let eventName: GA4EventType = 'pp_mobile_purchase';

    if ([1, 2].includes(step)) {
      eventName = `pp_mobile_step_${step}` as GA4EventType;
    }

    if ([2, 3].includes(step)) {
      basicUserProperties.value = amount as number;

      if (data.transactionId) {
        basicUserProperties.transaction_id = transactionId as string;
      }

      topupData.item_category3 = topupType === 'phone_topup' ? 'Standard Refill' : 'Internet Top-Up';
      topupData.item_variant = `${amount} RSD`;
      topupData.price = Number(amount);
    }

    if (step === 3 && amount) {
      topupData.item_variant = `${amount / 100} RSD`;
      topupData.price = Number(amount / 100);
    }

    const ga4SelectInsuranceEvent: GA4EventObject = {
      name: eventName,
      params: {
        items: [topupData],
        currency: RSD,
      },
    };

    TagManager.dataLayer({
      dataLayer: {
        user_id: gtmUserId,
        events: [ga4SelectInsuranceEvent],
        ...DEFAULT_GA4_PROPS,
      } as GA4Event,
    });
  }

  public static pprInteraction(state: AppState, eventName: string, contentData: GA4PPRContentData) {
    const gtmUserId = state.session.gtmData?.gtmUserId;

    TagManager.dataLayer({
      dataLayer: {
        user_id: gtmUserId,
        events: [
          {
            name: eventName,
          },
        ],
        ...DEFAULT_GA4_PROPS,
        content_data: contentData,
      } as any,
    });
  }

}
